import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { fetchOrders, orderKeys } from '@/api/orders';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { ButtonLink } from '@/components/ui/Button';
import { EmptyState } from '@/components/system/EmptyState';
import { ErrorState } from '@/components/system/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';

/** Order history — BUILD-PLAN §15, §16, §41. */
export function OrdersPage() {
  usePageMetadata({ title: 'My Orders', description: 'Your Glanz Luxe order history.' });

  const {
    data: orders = [],
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: orderKeys.orders,
    queryFn: fetchOrders,
  });

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-6 lg:px-8 lg:py-16">
      <PageHeader eyebrow="My account" title="Orders" className="mb-10" />

      {isError ? (
        <ErrorState
          title="We couldn’t load your orders"
          description="Please try again in a moment."
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <div className="flex flex-col gap-4" role="status" aria-label="Loading orders">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="h-28 animate-pulse border border-line bg-ink/[0.09] motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No orders yet"
          description="When you place an order, it will appear here."
          action={<ButtonLink to="/shop">Explore the Collection</ButtonLink>}
        />
      ) : (
        <ul className="flex flex-col gap-4">
          {orders.map((order) => (
            <li key={order.orderNumber}>
              <Link
                to={`/orders/${order.orderNumber}/confirmation`}
                className="flex flex-wrap items-center justify-between gap-4 border border-line bg-surface p-6 transition-shadow hover:shadow-card"
              >
                <div>
                  <p className="font-display text-lg text-ink">{order.orderNumber}</p>
                  <p className="mt-1 text-xs text-muted">
                    {order.placedAt ? new Date(order.placedAt).toLocaleDateString() : ''} ·{' '}
                    {order.items.length} {order.items.length === 1 ? 'piece' : 'pieces'}
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <span className="text-xs uppercase tracking-[0.2em] text-accent">
                    {order.status.label}
                  </span>
                  {order.totals && (
                    <span className="text-sm text-ink">{order.totals.total.formatted}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
